import { useState, useEffect } from 'react';
import { useTelegram } from '../hooks/useTelegram';
import { getBotConfig, updateBotConfig } from '../utils/api';
import type { BotConfig, Service } from '@tg-miniapp/shared-types';

export function SettingsPage(): React.JSX.Element {
  const { tg, initData } = useTelegram();
  const [config, setConfig] = useState<BotConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>('info');

  useEffect(() => {
    async function load() {
      if (!initData) return;
      const res = await getBotConfig(initData);
      if (res.ok) setConfig(res.value);
      setLoading(false);
    }
    load();
  }, [initData]);

  useEffect(() => {
    if (!config || !tg) return;
    
    tg.MainButton.setText('Сохранить изменения');
    tg.MainButton.show();
    
    const onSave = async () => {
      const res = await updateBotConfig(initData, config);
      if (res.ok) {
        tg.HapticFeedback.notificationOccurred('success');
        tg.showAlert('Сохранено! ✅');
      } else {
        tg.showAlert('Ошибка: ' + res.error.message);
      }
    };

    tg.MainButton.onClick(onSave);
    return () => {
      tg.MainButton.offClick(onSave);
      tg.MainButton.hide();
    };
  }, [config, initData, tg]);

  if (loading) return <div className="p-20 text-center animate-pulse">✨ Загрузка настроек...</div>;
  if (!config) return <div className="p-10 text-center">Ошибка загрузки</div>;

  const toggle = (key: string) => setOpen(open === key ? null : key);

  return (
    <div className="px-5 pt-4 pb-32 animate-in fade-in">
      <h1 className="text-3xl font-bold mb-8">⚙️ Настройки</h1>

      {/* О бизнесе */}
      <div className="bg-tg-theme-bg rounded-3xl shadow-sm border border-black/5 overflow-hidden mb-6">
        <button 
          onClick={() => toggle('info')}
          className="w-full flex items-center justify-between p-5 active:bg-black/5 transition-colors"
        >
          <div className="flex items-center gap-4">
            <span className="text-2xl">🏢</span>
            <div className="text-left">
              <div className="font-bold text-lg">Профиль бизнеса</div>
              <div className="text-xs text-tg-theme-hint">Название и описание</div>
            </div>
          </div>
          <span className={`text-xl transition-transform ${open === 'info' ? 'rotate-180' : ''}`}>▾</span>
        </button>
        
        {open === 'info' && (
          <div className="px-5 pb-6 pt-2 space-y-5 animate-in slide-in-from-top duration-300">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-tg-theme-hint mb-2 ml-1">Название компании</label>
              <input 
                className="w-full bg-black/5 dark:bg-white/5 border-none rounded-2xl px-4 py-4 text-lg font-bold outline-none focus:ring-2 ring-blue-500 transition-all"
                value={config.businessName}
                onChange={(e) => setConfig({ ...config, businessName: e.target.value })}
                placeholder="Например: Barbershop 'Style'"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-tg-theme-hint mb-2 ml-1">Описание (Обрезаться не будет)</label>
              <textarea 
                className="w-full bg-black/5 dark:bg-white/5 border-none rounded-2xl px-4 py-4 text-base outline-none focus:ring-2 ring-blue-500 transition-all min-h-[120px]"
                value={config.businessDescription || ''}
                onChange={(e) => setConfig({ ...config, businessDescription: e.target.value })}
                placeholder="Расскажите о вашем бизнесе клиентам..."
              />
            </div>
          </div>
        )}
      </div>

      {/* Услуги */}
      <div className="bg-tg-theme-bg rounded-3xl shadow-sm border border-black/5 overflow-hidden mb-6">
        <button 
          onClick={() => toggle('svc')}
          className="w-full flex items-center justify-between p-5 active:bg-black/5 transition-colors"
        >
          <div className="flex items-center gap-4">
            <span className="text-2xl">💈</span>
            <div className="text-left">
              <div className="font-bold text-lg">Услуги</div>
              <div className="text-xs text-tg-theme-hint">{config.services.length} активно</div>
            </div>
          </div>
          <span className={`text-xl transition-transform ${open === 'svc' ? 'rotate-180' : ''}`}>▾</span>
        </button>
        
        {open === 'svc' && (
          <div className="px-5 pb-6 pt-2 space-y-4 animate-in slide-in-from-top duration-300">
            {config.services.map((s, idx) => (
              <div key={s.id} className="bg-black/5 dark:bg-white/5 p-5 rounded-[2.5rem] relative group border border-transparent hover:border-blue-500/20 transition-all shadow-inner">
                <button 
                  onClick={() => {
                    const newServices = config.services.filter((_, i) => i !== idx);
                    setConfig({ ...config, services: newServices });
                  }}
                  className="absolute top-4 right-4 w-8 h-8 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center transition-colors hover:bg-red-500 hover:text-white"
                >
                  ✕
                </button>
                
                <div className="mb-4">
                  <label className="block text-[10px] font-bold text-tg-theme-hint uppercase mb-2 ml-1">Название услуги</label>
                  <input 
                    className="w-full bg-white/50 dark:bg-black/20 rounded-2xl px-4 py-3 font-bold text-lg outline-none focus:ring-2 ring-blue-500/30"
                    value={s.name}
                    onChange={(e) => {
                      const newServices = [...config.services];
                      newServices[idx] = { ...newServices[idx], name: e.target.value } as Service;
                      setConfig({ ...config, services: newServices });
                    }}
                    placeholder="Например: Стрижка мужская"
                  />
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-[10px] font-bold text-tg-theme-hint uppercase mb-2 ml-1">Стоимость (₽)</label>
                    <div className="relative">
                      <input 
                        type="number"
                        className="w-full bg-white/50 dark:bg-black/20 rounded-2xl px-4 py-3 font-black text-xl outline-none focus:ring-2 ring-blue-500/30"
                        value={s.price}
                        onChange={(e) => {
                          const newServices = [...config.services];
                          newServices[idx] = { ...newServices[idx], price: Number(e.target.value) } as Service;
                          setConfig({ ...config, services: newServices });
                        }}
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 opacity-30">₽</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="block text-[10px] font-bold text-tg-theme-hint uppercase mb-2 ml-1">Время (мин)</label>
                    <div className="relative">
                      <input 
                        type="number"
                        className="w-full bg-white/50 dark:bg-black/20 rounded-2xl px-4 py-3 font-black text-xl outline-none focus:ring-2 ring-blue-500/30"
                        value={s.durationMinutes}
                        onChange={(e) => {
                          const newServices = [...config.services];
                          newServices[idx] = { ...newServices[idx], durationMinutes: Number(e.target.value) } as Service;
                          setConfig({ ...config, services: newServices });
                        }}
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 opacity-30">⏳</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <button 
              onClick={() => {
                const newSvc: Service = { 
                  id: Math.random().toString(36).substr(2, 9), 
                  name: 'Новая услуга', 
                  price: 1000, 
                  durationMinutes: 60,
                  category: null 
                };
                setConfig({ ...config, services: [...config.services, newSvc] });
              }}
              className="w-full py-4 rounded-2xl border-2 border-dashed border-blue-500/30 text-blue-500 font-bold hover:bg-blue-500/5 transition-colors"
            >
              + Добавить услугу
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
